/**
 * User Profile Page
 * 
 * Displays user profile, settings, and usage statistics.
 */
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import ThreediumSettings from '../../components/ThreediumSettings';
import './Profile.css';
import { getAuthToken } from '../../utils/auth';

const CLOUD_API_BASE = process.env.REACT_APP_CLOUD_API_URL || 'http://localhost:8080';

const fetchUsage = async () => {
    const token = getAuthToken();
    const response = await fetch(`${CLOUD_API_BASE}/api/user/usage`, {
        headers: { 'Authorization': `Bearer ${token}` }
    });
    if (!response.ok) throw new Error('Failed to fetch usage');
    return response.json();
};

export function ProfilePage() {
    const { user, updateProfile, logout } = useAuth();
    const [usage, setUsage] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [displayName, setDisplayName] = useState(user?.display_name || '');
    const [isSaving, setIsSaving] = useState(false);
    const [message, setMessage] = useState(null);

    useEffect(() => {
        const loadUsage = async () => {
            try {
                const data = await fetchUsage();
                setUsage(data);
            } catch (err) {
                console.error('Failed to load usage:', err);
            }
        };
        loadUsage();
    }, []);

    const handleSave = async () => {
        setIsSaving(true);
        const result = await updateProfile({ display_name: displayName });
        setIsSaving(false);

        if (result.success) {
            setIsEditing(false);
            setMessage({ type: 'success', text: 'Profile updated successfully!' });
        } else {
            setMessage({ type: 'error', text: result.error });
        }

        setTimeout(() => setMessage(null), 3000);
    };

    const formatNumber = (num) => {
        if (num >= 1000000) return (num / 1000000).toFixed(2) + 'M';
        if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
        return num?.toString() || '0';
    };

    const usagePercentage = usage?.tier_limit_tokens
        ? Math.min(100, ((usage.current_month_input_tokens + usage.current_month_output_tokens) / usage.tier_limit_tokens) * 100)
        : 0;

    return (
        <div className="profile-page">
            <div className="profile-header">
                <h1>Profile & Settings</h1>
            </div>

            {message && (
                <div className={`profile-message ${message.type}`}>
                    {message.text}
                </div>
            )}

            <div className="profile-grid">
                {/* User Info Card */}
                <div className="profile-card">
                    <div className="card-header">
                        <h2>Profile Information</h2>
                        {!isEditing && (
                            <button onClick={() => setIsEditing(true)} className="edit-btn">
                                Edit
                            </button>
                        )}
                    </div>

                    <div className="profile-avatar-section">
                        <div className="profile-avatar">
                            {user?.avatar_url ? (
                                <img src={user.avatar_url} alt="" />
                            ) : (
                                <span>{(user?.display_name || user?.email || 'U')[0].toUpperCase()}</span>
                            )}
                        </div>
                        <div className="profile-basic">
                            {isEditing ? (
                                <input
                                    type="text"
                                    value={displayName}
                                    onChange={(e) => setDisplayName(e.target.value)}
                                    placeholder="Display name"
                                    className="name-input"
                                />
                            ) : (
                                <h3>{user?.display_name || 'No name set'}</h3>
                            )}
                            <p>{user?.email}</p>
                        </div>
                    </div>

                    <div className="profile-details">
                        <div className="detail-row">
                            <span className="label">Tier</span>
                            <span className={`tier-badge ${user?.tier}`}>
                                {user?.tier || 'free'}
                            </span>
                        </div>
                        <div className="detail-row">
                            <span className="label">Role</span>
                            <span className="value">{user?.role || 'user'}</span>
                        </div>
                        <div className="detail-row">
                            <span className="label">Member since</span>
                            <span className="value">
                                {user?.created_at
                                    ? new Date(user.created_at).toLocaleDateString()
                                    : 'N/A'}
                            </span>
                        </div>
                        <div className="detail-row">
                            <span className="label">Email verified</span>
                            <span className={`status ${user?.email_verified ? 'verified' : 'unverified'}`}>
                                {user?.email_verified ? '✓ Verified' : '✗ Not verified'}
                            </span>
                        </div>
                    </div>

                    {isEditing && (
                        <div className="edit-actions">
                            <button onClick={() => setIsEditing(false)} className="cancel-btn">
                                Cancel
                            </button>
                            <button onClick={handleSave} className="save-btn" disabled={isSaving}>
                                {isSaving ? 'Saving...' : 'Save Changes'}
                            </button>
                        </div>
                    )}
                </div>

                {/* Usage Card */}
                <div className="profile-card">
                    <div className="card-header">
                        <h2>Token Usage</h2>
                        <span className="period">This Month</span>
                    </div>

                    <div className="usage-overview">
                        <div className="usage-progress">
                            <div className="progress-bar">
                                <div
                                    className="progress-fill"
                                    style={{ width: `${usagePercentage}%` }}
                                />
                            </div>
                            <div className="progress-labels">
                                <span>
                                    {formatNumber(
                                        (usage?.current_month_input_tokens || 0) +
                                        (usage?.current_month_output_tokens || 0)
                                    )} used
                                </span>
                                <span>
                                    {usage?.tier_limit_tokens
                                        ? formatNumber(usage.tier_limit_tokens) + ' limit'
                                        : 'Unlimited'}
                                </span>
                            </div>
                        </div>
                    </div>

                    <div className="usage-breakdown">
                        <div className="usage-stat">
                            <span className="stat-label">Input Tokens</span>
                            <span className="stat-value">
                                {formatNumber(usage?.current_month_input_tokens || 0)}
                            </span>
                        </div>
                        <div className="usage-stat">
                            <span className="stat-label">Output Tokens</span>
                            <span className="stat-value">
                                {formatNumber(usage?.current_month_output_tokens || 0)}
                            </span>
                        </div>
                        <div className="usage-stat">
                            <span className="stat-label">Requests</span>
                            <span className="stat-value">
                                {formatNumber(usage?.current_month_requests || 0)}
                            </span>
                        </div>
                        <div className="usage-stat">
                            <span className="stat-label">Remaining</span>
                            <span className="stat-value highlight">
                                {usage?.tokens_remaining
                                    ? formatNumber(usage.tokens_remaining)
                                    : '∞'}
                            </span>
                        </div>
                    </div>

                    <div className="usage-total">
                        <span>All-time total</span>
                        <span>
                            {formatNumber(
                                (usage?.total_input_tokens || 0) +
                                (usage?.total_output_tokens || 0)
                            )} tokens
                        </span>
                    </div>
                </div>

                {/* Threedium Integration */}
                <div className="profile-card">
                    <ThreediumSettings />
                </div>

                {/* Account Actions */}
                <div className="profile-card danger-zone">
                    <div className="card-header">
                        <h2>Account</h2>
                    </div>

                    <div className="account-actions">
                        <button onClick={logout} className="logout-btn">
                            Sign Out
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default ProfilePage;

